import React, { useMemo } from "react";
import { injectIntl, intlShape } from "react-intl";
import { OrgLevelProductsAPI } from "@ai360/core";

import { OrgLevelSearch } from "~/admin/agBytes/components/org-level-search";
import { ZeroToInfiniteGrid } from "~/core";

import { messages } from "../../i18n-messages";
import { model } from "../../data";

type IHierarchyFilterLevelWithParentsGuid =
    OrgLevelProductsAPI.IHierarchyFilterLevelWithParentsGuid;

type OrgLevelListProps = {
    orgLevels: IHierarchyFilterLevelWithParentsGuid[];
    selectedOrgLevels: IHierarchyFilterLevelWithParentsGuid[];
    onSelectionChange: (
        items: IHierarchyFilterLevelWithParentsGuid[],
        removedOrgLevel: IHierarchyFilterLevelWithParentsGuid
    ) => void;
    intl: intlShape;
};

const OrgLevelList_ = ({
    orgLevels = [],
    intl,
    selectedOrgLevels,
    onSelectionChange,
}: OrgLevelListProps) => {
    const dropdownOptions = useMemo(() => {
        const ownerOrgLevel = orgLevels.find((item) => item.orgLevelParents.length === 0) || {
            orgLevelName: undefined,
        };
        return orgLevels.map((option) => {
            const parentContextString = option.orgLevelParents
                .filter((parent) => parent.name !== ownerOrgLevel.orgLevelName)
                .reduce((acc, orgLevel) => {
                    return (acc !== "" ? acc + " > " : acc) + orgLevel.name;
                }, "");
            return { ...option, parents: parentContextString };
        });
    }, [orgLevels]);

    const availableOptions = useMemo(() => {
        const selectedRecords = new Set(selectedOrgLevels.map((r) => r.orgLevelGuid));
        return dropdownOptions.filter((i) => !selectedRecords.has(i.orgLevelGuid));
    }, [selectedOrgLevels, dropdownOptions]);

    const onChange = (item: IHierarchyFilterLevelWithParentsGuid, isAdd) => {
        if (item == null) {
            return;
        }
        if (isAdd) {
            let selectedValues = [...selectedOrgLevels, item];
            if (item.orgLevelParents.length > 0) {
                const parentOrgLevels = new Set(item.orgLevelParents.map((p) => p.guid));
                selectedValues = selectedValues.filter((r) => !parentOrgLevels.has(r.orgLevelGuid));
            }
            selectedValues = selectedValues.filter(
                (r) => !r.orgLevelParents.some((p) => p.guid === item.orgLevelGuid)
            );
            onSelectionChange(selectedValues, null);
            return;
        }
        const removedOrgLevel = selectedOrgLevels.find((r) => r.orgLevelGuid === item.orgLevelGuid);
        const selectedValues = selectedOrgLevels.filter(
            (r) => r.orgLevelGuid !== item.orgLevelGuid
        );
        onSelectionChange(selectedValues, removedOrgLevel);
    };

    const { formatMessage } = intl;
    return (
        <div className={"form-section-child-stretch mini-grid auto-search-list-container"}>
            <OrgLevelSearch
                disabled={availableOptions.length === 0}
                classNames={[]}
                apiErrors={[]}
                clearOnSelection
                containerClassName={"org-level-search-container"}
                itemList={availableOptions}
                onSelection={(value) => onChange(value, true)}
                placeholderText={formatMessage(messages.addLocation)}
                required={true}
                selectedValue={selectedOrgLevels}
                subNameProps={["parents"]}
            />
            <ZeroToInfiniteGrid
                disabled={false}
                records={selectedOrgLevels}
                columns={{
                    [model.PROPS_ORG_LEVEL_NAME]: {
                        title: formatMessage(messages.name),
                        className: "org-level-name",
                    },
                    [model.PROPS_ORG_LEVEL_CITY]: {
                        title: formatMessage(messages.city),
                        className: "org-level-city",
                    },
                    [model.PROPS_ORG_LEVEL_STATE]: {
                        title: formatMessage(messages.state),
                        className: "org-level-state",
                    },
                }}
                className="org-level-grid"
                onDelete={(option) => onChange(option, false)}
            />
        </div>
    );
};

export const OrgLevelList = injectIntl(OrgLevelList_);
